import AppContext from "@common/contexts/App";
import { default as classnames } from "classnames";
import React from "react";

export type PreloaderType = "spinner" | "bars";

export type PreloaderColor = "default" | "white";
export interface PreloaderProps {
  className?: string;
  color?: PreloaderColor;
  isFontSize?: boolean;
  isFullScreen?: boolean;
  isTransparent?: boolean;
  isNotCentered?: boolean;
  loadingText?: string;
  relative?: boolean;
  size?: number;
  type?: PreloaderType;
}

const Preloader = (props: PreloaderProps) => {
  const { loadingText } = props;

  const appContext = React.useContext(AppContext);

  const type = React.useMemo(
    (): PreloaderType => props.type ?? (appContext.context === "admin" ? "bars" : "spinner"),
    [props.type, appContext.context]
  );

  // TODO: add string support
  const fontSize = React.useMemo(() => (props.size ? Math.min(props.size / 10, 10) : undefined), [props.size]);

  const containerStyle = React.useMemo((): React.CSSProperties => {
    if (type === "bars") {
      return {
        fontSize,
        height: props.size,
        width: props.size,
        margin: props.isNotCentered ? undefined : "auto"
      };
    }

    return {
      fontSize
    };
  }, [props.isNotCentered, type]);

  const style = React.useMemo((): React.CSSProperties => {
    if (type === "bars") {
      return {
        fontSize
      };
    }

    return {
      fontSize,
      height: props.size,
      width: props.size
    };
  }, [type]);

  return (
    <div
      className={classnames("preloader", props.className, type, props.color ?? "default", {
        absolute: props.relative === false,
        fixed_size: !!props.size,
        font_size: props.isFontSize,
        "full-screen": props.isFullScreen,
        relative: props.relative,
        transparent: props.isTransparent,
        visible: true
      })}
      style={containerStyle}
    >
      <div style={style} className="preloader_preload">
        <div className="left"></div>
        <div className="center"></div>
        <div className="right"></div>
      </div>

      {!!loadingText && <div className="loading_text">{loadingText}</div>}
    </div>
  );
};

export default Preloader;

type DelayPreloaderProps = PreloaderProps & {
  delayMS?: number;
};
export const DelayPreloader = ({ delayMS, ...props }: DelayPreloaderProps) => {
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), delayMS ?? 300);

    return () => clearTimeout(timeout);
  }, [delayMS, isVisible, setIsVisible]);

  if (!isVisible) return null;

  return <Preloader {...props} className="animated" />;
};

export const InlinePreloader = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <Preloader size={50} relative />
  </div>
);
